import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Send } from "lucide-react";

export default function MessageThread({ threadType, threadId, className = "" }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const meRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get(`/threads/${threadType}/${threadId}/messages`);
            setMessages(r.data || []);
        } catch { /* silent */ }
    };
    const loadMe = async () => {
        try { const r = await api.get("/auth/me"); meRef.current = r.data; } catch {/*nop*/}
    };

    useEffect(() => {
        loadMe();
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [threadType, threadId]);

    useEffect(() => {
        // Autoscroll to bottom on new messages
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault();
        const t = text.trim();
        if (!t) return;
        setSending(true);
        try {
            const r = await api.post(`/threads/${threadType}/${threadId}/messages`, { text: t });
            setMessages((prev) => [...prev, r.data]);
            setText("");
        } catch { /* toast handled globally elsewhere */ }
        finally { setSending(false); }
    };

    return (
        <div className={`flex flex-col border border-border bg-white ${className}`} data-testid={`chat-${threadType}-${threadId}`}>
            <div className="px-4 py-3 border-b border-border bg-neutral-50">
                <div className="overline text-neutral-600">Conversation</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 max-h-72 min-h-[8rem]" data-testid="chat-scroll">
                {messages.length === 0 && (
                    <div className="text-center text-xs text-neutral-400 font-body py-6">
                        No messages yet. Start the conversation.
                    </div>
                )}
                {messages.map((m) => {
                    const mine = meRef.current && m.from_id === meRef.current.id;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                            <div className={`max-w-[75%] px-3 py-2 border ${mine ? "bg-black text-white border-black" : "bg-neutral-50 text-black border-black/20"}`}>
                                <div className="text-[9px] uppercase tracking-widest opacity-60 mb-0.5">
                                    {m.from_name} · {m.from_role} · {new Date(m.at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
                                </div>
                                <div className="text-sm font-body whitespace-pre-wrap break-words">{m.text}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <form onSubmit={send} className="border-t border-border p-2 flex items-center gap-2">
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…"
                    className="flex-1 border border-black rounded-none h-10 px-3 text-sm font-body focus:outline-none"
                    data-testid="chat-input"
                />
                <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    className="inline-flex items-center gap-1 bg-black text-white uppercase tracking-widest text-[10px] font-semibold px-3 h-10 disabled:opacity-40"
                    data-testid="chat-send-btn"
                >
                    <Send className="h-3.5 w-3.5" strokeWidth={1.5}/> Send
                </button>
            </form>
        </div>
    );
}
